/**
 * Mount XR Assistant
 *
 * Lifecycle bridge: creates/disposes the XRAssistantPanel when the user
 * enters/exits immersive WebXR. Listens for `trace-viewer:xr-state-changed`
 * events dispatched by XRManager.
 */

import type { TraceScene } from 'trace-viewer/scene';
import type { Scene } from '@babylonjs/core/scene';
import { XRAssistantPanel } from './XRAssistantPanel';

export interface XRAssistantControls {
    dispose: () => void;
}

/**
 * Wire up the XR assistant panel to the XR session lifecycle.
 *
 * @param getTraceScene  – Returns the current TraceScene (may be null before load)
 * @param getBabylonScene – Returns the Babylon Scene (needed to create 3D GUI)
 */
export function mountXRAssistant(
    getTraceScene: () => TraceScene | null,
    getBabylonScene: () => Scene | null,
): XRAssistantControls {
    let panel: XRAssistantPanel | null = null;

    const onXRStateChanged = (event: Event) => {
        const detail = (event as CustomEvent<{ state?: string; isActive?: boolean }>).detail;

        if (detail?.isActive) {
            // Entering XR — create panel if we have a scene
            const traceScene = getTraceScene();
            const babylonScene = getBabylonScene();
            if (traceScene && babylonScene && !panel) {
                panel = new XRAssistantPanel(babylonScene, traceScene);
                panel.show();
                console.log('[XRAssistant] Panel created for XR session');
            }
        } else if (detail?.state === 'available' || detail?.state === 'not-supported') {
            // Exiting XR — dispose panel
            if (panel) {
                panel.dispose();
                panel = null;
                console.log('[XRAssistant] Panel disposed (XR session ended)');
            }
        }
    };

    window.addEventListener('trace-viewer:xr-state-changed', onXRStateChanged);

    return {
        dispose: () => {
            window.removeEventListener('trace-viewer:xr-state-changed', onXRStateChanged);
            if (panel) {
                panel.dispose();
                panel = null;
            }
        },
    };
}
